import React, {Component, Fragment} from 'react';
import {compose} from 'react-apollo';
import PropTypes from 'prop-types';
import glamorous from 'glamorous';
import {xor} from '../../../../shared/utils/lodash-functions';
import {
  withAnalyticsPayload,
  withSendAnalyticsEvent
} from '../../../../shared/enhancers/analytics-enhancer';
import Title from '../../../../shared/library/typography/title';
import {ALABASTER} from '../../../../shared/style/colors';
import SimpleButton from '../../../../shared/library/buttons/base/simple';
import {
  STACKUPS_SEARCH_QUERY,
  STACKUPS_SEARCH_SELECT_TOOL,
  STACKUPS_SEARCH_REMOVE_TOOL,
  STACKUPS_SEARCH_CLICK_COMPARE
} from '../../constants/analytics';
import ComparisonService from './comparison-service';
import ComparisonSeparator from './comparison-separator';
import ComparisonInput from './comparison-input';
import {ID} from '../../../../shared/utils/graphql';
import {PHONE_LANDSCAPE} from '../../../../shared/style/breakpoints';

const Container = glamorous.div({
  backgroundColor: ALABASTER,
  padding: '8px 36px 20px'
});

const BarTitle = glamorous(Title)({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: 12
});

const Wrapper = glamorous.div({
  display: 'flex',
  justifyContent: 'space-between',
  borderRadius: 4,
  [PHONE_LANDSCAPE]: {
    flexDirection: 'column'
  }
});

const CompareButton = glamorous(SimpleButton)({
  height: 'auto',
  marginLeft: 34,
  [PHONE_LANDSCAPE]: {
    margin: '10px auto'
  }
});

class ComparisonWidget extends Component {
  static propTypes = {
    services: PropTypes.arrayOf(
      PropTypes.shape({
        id: ID.isRequired,
        name: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
        image_url: PropTypes.string.isRequired
      }).isRequired
    ).isRequired,
    sendAnalyticsEvent: PropTypes.func.isRequired
  };

  state = {
    propsServices: null,
    slots: [0, 1, 2].map(index => this.props.services[index] || null)
  };

  setService = (index, service) => {
    const {sendAnalyticsEvent} = this.props;
    const {slots} = this.state;

    if (service) {
      sendAnalyticsEvent(STACKUPS_SEARCH_SELECT_TOOL, {tool: service});
    } else {
      sendAnalyticsEvent(STACKUPS_SEARCH_REMOVE_TOOL, {tool: slots[index]});
    }

    const nextSlots = [...slots];
    nextSlots[index] = service;

    this.setState({
      slots: nextSlots
    });
  };

  onCompareClick = () => {
    const {sendAnalyticsEvent} = this.props;
    const {slots} = this.state;

    const toolSlugs = slots
      .filter(Boolean)
      .map(s => s.slug)
      .sort();
    const stackupSlug = toolSlugs.join('-vs-');

    sendAnalyticsEvent(STACKUPS_SEARCH_CLICK_COMPARE, {stackupSlug, toolSlugs});

    window.location = `/stackups/${stackupSlug}`;
  };

  onSearch = query => {
    const {sendAnalyticsEvent} = this.props;

    sendAnalyticsEvent(STACKUPS_SEARCH_QUERY, {query});
  };

  render() {
    const {services} = this.props;
    const {slots} = this.state;

    const usedServices = slots.filter(Boolean);
    const usedSlugs = usedServices.map(s => s.slug);
    const compareEnabled =
      usedServices.length > 1 && xor(usedSlugs, services.map(s => s.slug)).length !== 0;
    const searchInputFilterFn = ({slug}) => !usedSlugs.includes(slug);

    return (
      <Container>
        <BarTitle>Customize your comparison</BarTitle>
        <Wrapper>
          {slots.map((service, index) => {
            return (
              <Fragment key={index}>
                {index > 0 ? <ComparisonSeparator /> : null}
                {service ? (
                  <ComparisonService
                    service={service}
                    onRemoveClick={() => this.setService(index, null)}
                  />
                ) : (
                  <ComparisonInput
                    placeholder="Add tool"
                    onSearch={this.onSearch}
                    onSearchItemAdded={service => this.setService(index, service)}
                    filterFn={searchInputFilterFn}
                    id={index}
                  />
                )}
              </Fragment>
            );
          })}
          <CompareButton
            data-testid="compare"
            onClick={compareEnabled ? this.onCompareClick : undefined}
            disabled={!compareEnabled}
          >
            Compare
          </CompareButton>
        </Wrapper>
      </Container>
    );
  }
}

export default compose(
  withAnalyticsPayload({path: window.location.pathname}),
  withSendAnalyticsEvent
)(ComparisonWidget);
